import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ResourceItem } from "@rago/web-shared";
import { onAccountEnd } from "../lib/accountScope";
import { resourcesQuery } from "../lib/resourcesQuery";
import { compareServers } from "../lib/serverOrder";
import { ServerSource } from "./serverSource";
import type { RagoSource } from "./types";

/**
 * Source registry: a live ServerSource per claimed, usable server (from
 * /v1/resources). Server instances are stable across refetches (a
 * module-level Map) so pinned connections and cached watch state survive.
 * getSources()/getSource() stay synchronous, backed by this Map, so player
 * and poster callbacks are unaffected; useSources() drives the query.
 *
 * "Usable" is this device's own verdict wherever it has one, and the
 * broker's presence only until then. Wider than presence: a server that
 * stopped checking in (its machine lost internet) but still answers on one
 * of its stored candidates — the owner's custom access URL behind a reverse
 * proxy, the LAN address — counts as online. And narrower (Kenny, 2026-10-03:
 * "shouldn't be online if it cannot be reached"): a server the broker heard
 * from minutes ago — presence lasts eleven after the last check-in — is NOT
 * online once this device has raced its candidates and nothing answered. The
 * broker keeps handing out the stored candidates regardless of presence, so
 * the registry itself dials every server the UI no longer does (it issues
 * no requests to a server it shows as unavailable) and promotes the ones
 * that answer. It also looks in on the ones it believes it can reach: a page
 * left open asks its server nothing, and would go on calling a dead one
 * online. The registry is itself an observable store (useRegistry) so a
 * reachability flip re-renders whoever derives "online" from it.
 */

const serverSources = new Map<string, ServerSource>();
const onlineIds = new Set<string>();
const brokerOnline = new Map<string, boolean>();
const unsubscribes = new Map<string, () => void>();
const lastKickAt = new Map<string, number>();
/** When each server is next re-verified; and, for one the broker hears from
 * but this device has lost, how many looks it has had since. */
const nextCheckAt = new Map<string, number>();
const lostLooks = new Map<string, number>();
const listeners = new Set<() => void>();

/** Gate between proactive races for one unattended server. */
const KICK_GAP_MS = 30_000;
/** How often the timer looks for a server that is due. */
const TICK_MS = 10_000;
/** Background re-verification cadence: every server with something to dial,
 * reached or not. */
const RECHECK_MS = 60_000;
/** A server the broker hears from but this device just failed to reach is
 * tried again sooner at first: a lost race is as often this device's network
 * waking up as the server being gone. Then the slow cadence. */
const SOON_MS = [10_000, 30_000];

/** The set of usable ids as a stable string — the store snapshot. */
let onlineKey = "";
let notifyQueued = false;
let recheckTimer: ReturnType<typeof setInterval> | null = null;

function hasCandidates(src: ServerSource): boolean {
  return src.conn.candidateCount() > 0;
}

function isUsable(id: string, src: ServerSource): boolean {
  if (!hasCandidates(src)) return false;
  if (src.conn.isReachable()) return true;
  // No proven path. The broker's word stands only until this device has
  // tried: a race that found nothing answering is the truer verdict.
  return brokerOnline.get(id) === true && !src.conn.isUnreachable();
}

/** Rebuild the usable set; notify (deferred) only when it changed. */
function recompute(): void {
  onlineIds.clear();
  for (const [id, src] of serverSources) {
    if (isUsable(id, src)) onlineIds.add(id);
  }
  // A connection's own emit can be what makes a server unattended (a request
  // failed, the re-race found nothing): the schedule and its timer follow.
  syncSchedule();
  syncRecheckTimer();
  const key = [...onlineIds].join("\n");
  if (key === onlineKey) return;
  onlineKey = key;
  // applyResources runs during a React render and connection emits are
  // themselves deferred — a sync notify here would setState mid-render.
  if (notifyQueued) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    for (const fn of listeners) fn();
  });
}

/** Servers with something to dial that nothing else dials: the broker has
 * stopped hearing from them, or this device could not reach them — either
 * way the UI sends them no requests, so only the registry can find out that
 * one is back. */
function unattended(): ServerSource[] {
  const out: ServerSource[] = [];
  for (const [id, src] of serverSources) {
    if (!hasCandidates(src)) continue;
    if (brokerOnline.get(id) !== true || src.conn.isUnreachable()) out.push(src);
  }
  return out;
}

/** Every server with something to dial: all of them are looked in on. */
function watched(): ServerSource[] {
  return [...serverSources.values()].filter(hasCandidates);
}

function delayFor(id: string): number {
  const looks = lostLooks.get(id);
  return looks === undefined ? RECHECK_MS : (SOON_MS[looks] ?? RECHECK_MS);
}

/** Give every watched server a next look. One this device has just lost
 * while the broker still hears from it gets its look soon, whatever was
 * scheduled; one that is back, or gone from the broker too, returns to the
 * slow cadence. */
function syncSchedule(): void {
  const now = Date.now();
  const live = new Set<string>();
  for (const src of watched()) {
    const id = src.id;
    live.add(id);
    const lost = brokerOnline.get(id) === true && src.conn.isUnreachable();
    if (!lost) {
      lostLooks.delete(id);
    } else if (!lostLooks.has(id)) {
      lostLooks.set(id, 0);
      nextCheckAt.set(id, now + delayFor(id));
      continue;
    }
    if (!nextCheckAt.has(id)) nextCheckAt.set(id, now + delayFor(id));
  }
  for (const id of nextCheckAt.keys()) {
    if (live.has(id)) continue;
    nextCheckAt.delete(id);
    lostLooks.delete(id);
  }
}

function recheck(src: ServerSource, now: number): void {
  const looks = lostLooks.get(src.id);
  if (looks !== undefined) lostLooks.set(src.id, looks + 1);
  nextCheckAt.set(src.id, now + delayFor(src.id));
  void src.conn.recheck();
}

function recheckDue(): void {
  const now = Date.now();
  for (const src of watched()) {
    if (now >= (nextCheckAt.get(src.id) ?? 0)) recheck(src, now);
  }
}

function recheckAll(): void {
  const now = Date.now();
  for (const src of watched()) recheck(src, now);
}

/** A hidden tab (background, or a window Chrome reports occluded) skips
 * ticks; catching up the moment it shows again keeps a dead server from
 * looking online for another interval. */
function onVisibilityChange(): void {
  if (!document.hidden) recheckAll();
}

function syncRecheckTimer(): void {
  const wanted = watched().length > 0;
  const hasDocument = typeof document !== "undefined";
  const hasWindow = typeof window !== "undefined";
  if (wanted && recheckTimer === null) {
    recheckTimer = setInterval(() => {
      if (hasDocument && document.hidden) return;
      recheckDue();
    }, TICK_MS);
    if (hasDocument) document.addEventListener("visibilitychange", onVisibilityChange);
    // The network came back (a wake, a Wi-Fi change): the likeliest moment
    // for a server this device had just lost to answer again.
    if (hasWindow) window.addEventListener("online", recheckAll);
  } else if (!wanted && recheckTimer !== null) {
    clearInterval(recheckTimer);
    recheckTimer = null;
    if (hasDocument) document.removeEventListener("visibilitychange", onVisibilityChange);
    if (hasWindow) window.removeEventListener("online", recheckAll);
  }
}

/** Reconcile the registry from a resources payload. */
export function applyResources(items: ResourceItem[]): void {
  const seen = new Set<string>();
  // Heap-order payloads must not decide source order (sources[0] and
  // sourceKey join keys flip otherwise) — oldest claim first, the same
  // order useServers() presents.
  const ordered = [...items].sort((a, b) =>
    compareServers(
      { claimedAt: a.claimedAt, id: a.serverId },
      { claimedAt: b.claimedAt, id: b.serverId },
    ),
  );
  for (const r of ordered) {
    seen.add(r.serverId);
    const existing = serverSources.get(r.serverId);
    if (existing) {
      existing.update(r);
    } else {
      const src = new ServerSource(r);
      serverSources.set(r.serverId, src);
      unsubscribes.set(r.serverId, src.conn.subscribe(recompute));
    }
    brokerOnline.set(r.serverId, r.presence.online);
  }
  // Deleting the current entry mid-iteration is safe for a Map.
  for (const id of serverSources.keys()) {
    if (seen.has(id)) continue;
    serverSources.delete(id);
    brokerOnline.delete(id);
    lastKickAt.delete(id);
    nextCheckAt.delete(id);
    lostLooks.delete(id);
    unsubscribes.get(id)?.();
    unsubscribes.delete(id);
  }
  recompute();
  // Race the stored candidates of every unattended server that has not
  // proven a path — nothing else would dial it (the UI issues no requests
  // to a server it considers offline). Deferred: same render rule.
  const now = Date.now();
  for (const src of unattended()) {
    const id = src.id;
    if (src.conn.isReachable()) continue;
    if (now - (lastKickAt.get(id) ?? 0) < KICK_GAP_MS) continue;
    lastKickAt.set(id, now);
    queueMicrotask(() => void src.conn.establish(true).catch(() => {}));
  }
  syncRecheckTimer();
}

// The account ended or changed: its servers go at once, with the tokens
// minted for it, their watch state and the timer that looks in on them. The
// next account's list builds its own, also for a server both can use.
onAccountEnd(() => applyResources([]));

/** All active sources: the usable servers (empty until one is). */
export function getSources(): RagoSource[] {
  return [...onlineIds]
    .map((id) => serverSources.get(id))
    .filter((s): s is ServerSource => Boolean(s));
}

export function getSource(id: string): RagoSource | null {
  return serverSources.get(id) ?? null;
}

/** True when at least one real server is usable (demo hidden). */
export function hasOnlineServer(): boolean {
  return onlineIds.size > 0;
}

/** Effective online: this device reaches it, or the broker sees it and
 * this device has not yet tried and failed. */
export function isServerOnline(id: string): boolean {
  return onlineIds.has(id);
}

export function subscribeRegistry(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function registrySnapshot(): string {
  return onlineKey;
}

/** Re-render when the usable set changes (a direct path proven or lost). */
export function useRegistry(): string {
  return useSyncExternalStore(subscribeRegistry, registrySnapshot, registrySnapshot);
}

/** Test seam: drop every server and timer. */
export function resetRegistryForTests(): void {
  for (const fn of unsubscribes.values()) fn();
  unsubscribes.clear();
  serverSources.clear();
  brokerOnline.clear();
  lastKickAt.clear();
  nextCheckAt.clear();
  lostLooks.clear();
  onlineIds.clear();
  onlineKey = "";
  if (recheckTimer !== null) {
    clearInterval(recheckTimer);
    recheckTimer = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    if (typeof window !== "undefined") window.removeEventListener("online", recheckAll);
  }
}

/** React hook: keeps the registry synced and returns the active sources. */
export function useSources(): { sources: RagoSource[]; isLoading: boolean } {
  const query = useQuery({
    ...resourcesQuery,
    // An online server with no candidates yet is a fresh claim mid-cert;
    // poll fast so it becomes reachable without a reload. (Local mode: the
    // same interval is a same-origin liveness ping of the one server.)
    refetchInterval: (q) =>
      q.state.data?.items.some(
        (i) => i.owned && i.presence.online && (i.connections?.length ?? 0) === 0,
      )
        ? 15_000
        : 60_000,
    staleTime: 30_000,
  });
  applyResources(query.data?.items ?? []);
  useRegistry();
  return { sources: getSources(), isLoading: query.isLoading };
}
